import { Component, ContentChild, HostListener, Inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { SpotterSharedComponentsModule } from '@spottercomponents/shared-components.module';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SidebarComponent, SidebarLayout } from "@spottercomponents/layout/sidebar/sidebar.component";
import { CommonModule } from '@angular/common';
import { FooterComponent } from "../footer/footer.component";
import { HeaderComponent } from '../header/header.component';
import { AuthService } from '@spotter/services/user/auth-service';
import { MatomoTracker } from 'ngx-matomo-client';
import { TrackerEvents } from '@spotter/models/analytics';
import { LanguageService } from '@spotter/services/internal/language-service';
import { ToastService } from '@spotter/services/internal/toast-service';
import { NgbModal, NgbOffcanvas, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { EventService } from '@spotter/services/internal/event-service';
//import { environment } from 'src/environments/environment';
import { filter } from 'rxjs';
import { SPOTTER_ENV } from '@spotter/tokens/env.token';
import { SpotterEnvironment } from '@spotter/environments/environment.template';

@Component({
    selector: 'main-layout',
    standalone: true,
    imports: [SidebarComponent, RouterModule, CommonModule, FooterComponent, HeaderComponent, NgbToastModule, TranslateModule],
    templateUrl: './main-layout.component.html',
    styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent {
    @Input() sidebarLayout!: SidebarLayout;
    @ContentChild('sidebarContent', { static: false }) sidebarContentTemplate: TemplateRef<any> | null = null;

    /** Custom content to add to the dropdown in the navbar when the user clicks their avatar. */
    @Input() navbarUserDropdownContent: TemplateRef<any> | null = null;


    serverErrorString: string = "";

    @Input() site!: "spotter" | "restorer" | 'tracker';

    @ViewChild('serverErrorModal', { read: TemplateRef }) serverErrorModal!: TemplateRef<any>;
    @ViewChild('serverDownModal', { read: TemplateRef }) serverDownModal!: TemplateRef<any>;
    @ViewChild('throttleModal', { read: TemplateRef }) throttleModal!: TemplateRef<any>;

    showDesktopSidebar = true;

    constructor(public auth: AuthService, private tracker: MatomoTracker, public languageSevice: LanguageService, public toastService: ToastService, private offcanvasService: NgbOffcanvas, private events: EventService, private router: Router, private modal: NgbModal, @Inject(SPOTTER_ENV) private env: SpotterEnvironment) {

    }


    ngOnInit() {
        window.dispatchEvent(new Event('resize')); // Trigger a resize event so the sidebar can update
        this.setupEventListeners();

        if (this.env.envName != 'prod')
            console.warn(`The website is running in ${this.env.envName} mode`);

        this.tracker.enableHeartBeatTimer(5);


    }

    /** When the window is resized, we handle the changes in the sidebar */
    @HostListener('window:resize', ['$event'])
    onResize(event: any) {
        this.showDesktopSidebar = event.target.innerWidth >= 992;

        if (this.showDesktopSidebar && this.offcanvasService.hasOpenOffcanvas())
            this.offcanvasService.dismiss();
    }


    logout() {
        this.auth.logoutToken().then(() => {
            this.tracker.trackEvent(TrackerEvents.c_Auth, TrackerEvents.a_Auth_Logout);
            window.location.reload();
        });
    }


    /** Opens the off canvas sidebar menu on mobile */
    openOffCanvas(content: TemplateRef<any>) {
        this.offcanvasService.open(content, { panelClass: 'sidebar-off-canvas' });
    }

    /** Sets up the various event listeners to show modals redirections etc. */
    setupEventListeners() {

        // Close all modal windows when navigating away from page
        this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((ev: NavigationEnd) => {
            this.modal.dismissAll();
            this.offcanvasService.dismiss();
        });

        // Check we're still logged in if the window gains focus
        window.onfocus = () => { this.auth.checkSessionStillActive(); }


        // Redirect any 404 errors to the 404 page
        this.events.fail404.subscribe(
            response => {
                console.error(response);
                this.router.navigateByUrl('/404');
            }
        );

        // Redirect to login if we are unauthenticated
        this.events.fail401.subscribe(
            () => {
                console.error("401 error caught - redirecting to login page");
                if (this.auth.returnUrlAfterLogin == "")
                    this.auth.returnUrlAfterLogin = this.router.routerState.snapshot.url;
                console.log(this.auth.returnUrlAfterLogin);

                this.router.navigateByUrl('/login');
            }
        );

        // Not an admin
        this.events.fail403.subscribe(
            response => {
                console.warn("403 error caught by main component");
                this.router.navigateByUrl('/');
            }
        );

        // Server error
        this.events.fail500.subscribe(
            response => {
                this.modal.open(this.serverErrorModal);
                this.serverErrorString = response.message;
            }
        );

        // Server down
        this.events.fail503.subscribe(() => { this.modal.open(this.serverDownModal); });

        // Too many requests
        this.events.fail429.subscribe(() => { this.modal.open(this.throttleModal); });
    }


}
